import moment from "moment";

import {ATTITUDES} from "@/utils/scoutingData";
import {getVerdict, reportAverage, scoreTone} from "@/utils/scoutingReports";

import "./index.scss";

const formatAverage = (value) =>
	value === null ? "—" : value.toFixed(1).replace(".", ",");

function ScoreBar({label, value}) {
	const tone = value ? scoreTone(value) : "empty";
	return (
		<div className="srd-score">
			<span className="srd-score__label">{label}</span>
			<span
				className={"srd-score__bar srd-score__bar--" + tone}
				role="img"
				aria-label={label + ": " + (value ? value + " su 5" : "non valutato")}
			>
				{[1, 2, 3, 4, 5].map((n) => (
					<span key={n} className={n <= (value || 0) ? "is-on" : ""} />
				))}
			</span>
			<span className="srd-score__value">{value || "—"}</span>
		</div>
	);
}

function Tags({items}) {
	return (
		<div className="srd-tags">
			{items.map((item) => (
				<span
					key={item.id}
					className="srd-tag"
					style={item.color ? {"--tag-color": item.color} : undefined}
				>
					{item.label}
				</span>
			))}
		</div>
	);
}

// Scheda di una relazione salvata, letta dalla copia del template.
export default function ScoutReportDetail({
	report,
	otherReports = [],
	onOpenReport,
}) {
	const fields = report?.template?.fields || [];
	const values = report?.values || {};
	const verdict = getVerdict(report?.verdict);
	const average = reportAverage(report);
	const player = report?.player || {};
	const scores = fields.filter((f) => f.type === "score");
	const tagFields = fields.filter(
		(f) =>
			(f.type === "attitude" || f.type === "choice") &&
			[].concat(values[f.id] || []).length,
	);
	const texts = fields.filter(
		(f) => f.type === "text" && String(values[f.id] || "").trim(),
	);

	return (
		<div className="scout-report-detail">
			<header className="srd-head">
				<div>
					<div className="srd-head__player">
						{[player.lastName, player.firstName].filter(Boolean).join(" ") ||
							"Giocatore"}
					</div>
					<div className="srd-head__meta">
						{[
							report?.template?.name,
							report?.reportDate &&
								moment(report.reportDate).format("DD/MM/YYYY"),
							report?.match?.name,
						]
							.filter(Boolean)
							.join(" · ")}
					</div>
				</div>
				{report?.author?.name && (
					<div className="srd-head__author">
						Osservatore
						<strong>{report.author.name}</strong>
					</div>
				)}
			</header>

			<section className="srd-band">
				<div
					className="srd-verdict"
					style={
						verdict
							? {"--verdict-color": verdict.color, "--verdict-bg": verdict.bg}
							: undefined
					}
				>
					<span className="srd-verdict__label">
						{verdict?.label || "Senza verdetto"}
					</span>
					<span className="srd-verdict__average">
						Media <strong>{formatAverage(average)}</strong>
					</span>
				</div>
				<p className="srd-judgment">{report?.judgment}</p>
			</section>

			{!!scores.length && (
				<section className="srd-panel">
					<h3>Voti</h3>
					{scores.map((f) => (
						<ScoreBar key={f.id} label={f.label} value={values[f.id]} />
					))}
				</section>
			)}

			{tagFields.map((f) => (
				<section key={f.id} className="srd-panel">
					<h3>{f.label}</h3>
					<Tags
						items={[].concat(values[f.id]).map((id) => {
							const attitude =
								f.type === "attitude" && ATTITUDES.find((a) => a.id === id);
							return attitude
								? {
										id,
										label: id + " · " + attitude.label,
										color: attitude.color,
									}
								: {id, label: id};
						})}
					/>
				</section>
			))}

			{texts.map((f) => (
				<section key={f.id} className="srd-panel">
					<h3>{f.label}</h3>
					<p className="srd-text">{values[f.id]}</p>
				</section>
			))}

			{report?.statsSnapshot && (
				<section className="srd-panel">
					<h3>Statistiche al momento della relazione</h3>
					<p className="srd-text">
						{[
							report.statsSnapshot.seasonName,
							report.statsSnapshot.matches + " gare",
							report.statsSnapshot.minutes + "'",
							report.statsSnapshot.goals + " gol",
							report.statsSnapshot.assists + " assist",
							report.statsSnapshot.xg != null &&
								"xG " + report.statsSnapshot.xg.toFixed(2).replace(".", ","),
						]
							.filter(Boolean)
							.join(" · ")}
					</p>
				</section>
			)}

			{!!otherReports.length && (
				<section className="srd-panel">
					<h3>Altre relazioni</h3>
					{otherReports.map((r) => {
						const v = getVerdict(r.verdict);
						return (
							<button
								key={r.id}
								type="button"
								className="srd-other"
								onClick={() => onOpenReport && onOpenReport(r)}
							>
								<span>
									{r.reportDate
										? moment(r.reportDate).format("DD/MM/YYYY")
										: ""}
									{r.author?.name ? " · " + r.author.name : ""}
								</span>
								<span
									className="srd-other__verdict"
									style={v ? {color: v.color} : undefined}
								>
									{v?.label || "—"} · {formatAverage(reportAverage(r))}
								</span>
							</button>
						);
					})}
				</section>
			)}
		</div>
	);
}
